import { Alert } from "@mui/material";
import { TitleAndNameGroupShadcn } from "../../../form-input-components/groups/title-and-name.group";
import { usePatientForm } from "./PatientFormContext";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { CustomTextFieldShadcn } from "../../../form-input-components/custom-input-components/custom-text-field-shadcn.component";
import { TextDateInputDelayedSubmission } from "@/dentlab/src/components/ui/text-date-input";
import { InputWrapper } from "@/dentlab/src/components/input-wrapper";
import { cn } from "@/dentlab/src/lib/utils/cn";
import { supabase } from "@/lib/supabase";
import { useJobStore } from "@/dentlab/src/store/Jobs";

export const PatientCoreForm: React.FC<{
	className?: string;
	autoFocusCode?: boolean;
}> = ({ className, autoFocusCode = false }) => {
	const {
		formData,
		formErrors,
		setFormErrors,
		handleInputChange,
		handleValueChange,
		isUpdate,
	} = usePatientForm();
	const { profile } = useCentralStore((state) => ({
		profile: state.profile,
	}));
	const isPrivacyOn = !!profile?.data_privacy_mode;

	const job = useJobStore((state) => state.job);
	const organizationId = useCentralStore((state) => state.organization?.id);

	const handleCodeChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const newCode = event.target.value;
		handleInputChange(event);

		// Without clearing the error, it will persist as the useForm hook only clears errors for fields that have
		// standard validation.
		setFormErrors(prev => ({ ...prev, code: null }));

		// Check for duplicate code for that client
		if (newCode && organizationId && formData.id) {
			const { data, error } = await supabase
				.from("patients")
				.select("*")
				.eq("code", newCode)
				.eq("organization_id", organizationId)
				.eq("client_id", job?.client_id ?? "")
				.neq("id", formData.id)
				.limit(1);

			if (!error && data && data.length > 0) {
				// Set form error to prevent autosave
				setFormErrors(prev => ({ ...prev, code: "Ein Patient mit diesem Code existiert bereits für diesen Kunden." }))
			}
		}
	};

	return (
		<div className={cn("flex flex-col gap-1", className)}>
			{isPrivacyOn ? (
				<Alert
					severity="info"
					sx={{
						marginTop: "10px",
					}}
				>
					Name-Feld im Datenschutz-Modus nicht verfügbar
				</Alert>
			) : (
				<TitleAndNameGroupShadcn
					formData={formData}
					formErrors={formErrors}
					onChange={handleInputChange}
					direction="row"
					onValueChange={(args: any) =>
						handleValueChange(args.name, args.value)
					}
				/>
			)}

			<div className="flex flex-row gap-2 items-center w-full">
				<CustomTextFieldShadcn
					label="Code"
					name="code"
					customHelperText="Patientennummer beim Zahnarzt."
					value={formData.code || ""}
					onChange={handleCodeChange}
					validationError={formErrors.code}
					autoFocus={autoFocusCode}
					// When creating a new patient and the code is not set, it's created on the database
					// However, when updating a patient, the code is not set on the database and should be required
					required={isUpdate}
					classNameWrapper="w-[50%]"
				/>
				<InputWrapper label="Geburtsdatum" className="w-[50%]">
					<TextDateInputDelayedSubmission
						value={formData.birth_date || ""}
						onDateChange={(date) =>
							handleValueChange("birth_date", date)
						}
					/>
				</InputWrapper>
			</div>
		</div>
	);
};
